import { cur } from '@/lib/currency';
import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Link as LinkIcon, Save, Trash2, Search, Sparkles, ArrowLeft, AlertCircle, Plus, ChevronRight, ChevronDown } from 'lucide-react';
import api from '@/lib/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MappingReviewView } from '../components/accounting/MappingReviewView';

export default function CategoryAccountMappingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [categoriesInUse, setCategoriesInUse] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState({ category: '', account_id: '', account_name: '' });
  const [autoMapping, setAutoMapping] = useState(false);
  const [view, setView] = useState('simple');
  const [reviewKey, setReviewKey] = useState(0);

  const runAutoMap = async () => {
    setAutoMapping(true);
    try {
      const preview = await api.post('/accounting/category-account-mappings/auto-map', { dry_run: true });
      const m = preview.data?.mapped_count || 0;
      const s = preview.data?.skipped_count || 0;
      if (m === 0) { toast.info(`No clearly-named unmapped categories to auto-map (${s} skipped).`); return; }
      const sample = (preview.data?.mapped || []).slice(0, 6).map(x => `• ${x.category} → ${x.account_name}`).join('\n');
      if (!window.confirm(`Auto-map ${m} categor${m === 1 ? 'y' : 'ies'} to matching accounts?\n\n${sample}${m > 6 ? `\n…and ${m - 6} more` : ''}\n\n(${s} ambiguous left unmapped. You can edit any mapping afterwards.)`)) return;
      const res = await api.post('/accounting/category-account-mappings/auto-map', { dry_run: false });
      toast.success(`Auto-mapped ${res.data?.mapped_count || 0} categories${(res.data?.skipped_count || 0) ? `, ${res.data.skipped_count} left for manual review` : ''}.`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-map failed');
    } finally { setAutoMapping(false); }
  };

  const runRefix = async () => {
    setAutoMapping(true);
    try {
      const preview = await api.post('/accounting/category-account-mappings/auto-map', { dry_run: true, include_auto_mapped: true });
      const changes = (preview.data?.mapped || []).filter(x => x.previous_account);
      const newOnes = (preview.data?.mapped || []).filter(x => !x.previous_account);
      const total = (preview.data?.mapped_count || 0);
      if (total === 0) { toast.info('Nothing to re-fix — all auto-mapped categories already match the latest logic.'); return; }
      const sample = (preview.data?.mapped || []).slice(0, 8).map(x => x.previous_account ? `• ${x.category}: ${x.previous_account} → ${x.account_name}` : `• ${x.category} → ${x.account_name} (new)`).join('\n');
      if (!window.confirm(`Re-fix ${total} mapping${total === 1 ? '' : 's'}? (${changes.length} corrected, ${newOnes.length} newly mapped)\n\n${sample}${total > 8 ? `\n…and ${total - 8} more` : ''}\n\nManually-edited mappings are never touched.`)) return;
      const res = await api.post('/accounting/category-account-mappings/auto-map', { dry_run: false, include_auto_mapped: true });
      toast.success(`Re-fixed ${res.data?.mapped_count || 0} mapping(s).`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-fix failed');
    } finally { setAutoMapping(false); }
  };

  const groupUtilities = async () => {
    setAutoMapping(true);
    try {
      const prev = await api.post('/accounting/restructure-utilities', { dry_run: true });
      const p = prev.data?.plan || {};
      const lines = [];
      if (p.create_parent) lines.push('• Create parent account "Utilities"');
      (p.create_children || []).forEach(c => lines.push(`• Create child account "${c}"`));
      (p.reparent || []).forEach(r => lines.push(`• Move "${r}" under Utilities`));
      if (lines.length === 0) { toast.info('Chart structure already organized. ✓'); return; }
      if (!window.confirm(`Organize the chart of accounts?\n\n${lines.join('\n')}\n\nSafe — existing entries & mappings are preserved.`)) return;
      await api.post('/accounting/restructure-utilities', { dry_run: false });
      toast.success('Chart organized (Utilities grouped + Supplier Payable added). Now click "Re-fix mappings" to route categories correctly.');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not group utilities');
    } finally { setAutoMapping(false); }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [mapsRes, catsRes, accRes, treeRes] = await Promise.allSettled([
        api.get('/accounting/category-account-mappings'),
        api.get('/accounting/category-account-mappings/categories-in-use'),
        api.get('/accounting/accounts'),
        api.get('/accounting/expense-category-tree'),
      ]);
      if (mapsRes.status === 'fulfilled') {
        setMappings(mapsRes.value.data?.mappings || []);
        setDefaults(mapsRes.value.data?.defaults || []);
      }
      if (catsRes.status === 'fulfilled') setCategoriesInUse(catsRes.value.data?.categories || []);
      if (accRes.status === 'fulfilled') setAccounts((accRes.value.data || []).filter(a => a.type === 'expense' || a.type === 'liability' || a.type === 'asset'));
      if (treeRes.status === 'fulfilled') setTree(treeRes.value.data?.tree || []);
    } catch {
      toast.error('Failed to load mappings');
    } finally { setLoading(false); }
  };

  const [tree, setTree] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [showSubDialog, setShowSubDialog] = useState(null); // { parentId, parentName, level }
  const [newSubName, setNewSubName] = useState('');

  const toggleExpand = (key) => {
    setExpandedKeys(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const addSubCategory = async () => {
    if (!showSubDialog || !newSubName.trim()) { toast.error('Enter a name'); return; }
    try {
      await api.post('/categories', {
        name: newSubName.trim(),
        type: 'expense',
        parent_id: showSubDialog.parentId,
      });
      toast.success(`Added "${newSubName.trim()}" under ${showSubDialog.parentName}`);
      setShowSubDialog(null);
      setNewSubName('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add subcategory');
    }
  };

  const deleteSubCategory = async (cat) => {
    if (!window.confirm(`Delete subcategory "${cat.name}"? Any of its children will also be removed.`)) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success('Removed');
      loadAll();
    } catch { toast.error('Delete failed'); }
  };

  useEffect(() => { loadAll(); }, []);
  // iter933 — deep-link from App Audit "Map Now": pre-filter to that category.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setSearch(cat);
  }, [searchParams]);

  // Merge customMapping (tenant) + defaults (fallback) + categoriesInUse (discoverable) + tree (defined main+sub even if never used yet)
  const rows = useMemo(() => {
    const byCat = {};
    // Start with defaults (so built-in categories always show)
    for (const d of defaults) {
      byCat[d.category] = { category: d.category, account_name: d.account_name, is_default: true, custom: false };
    }
    // Overlay custom mappings
    for (const m of mappings) {
      byCat[m.category] = { ...m, is_default: false, custom: true };
    }
    // Add unmapped used-categories so user can quickly route them
    for (const c of categoriesInUse) {
      const key = (c.category || '').toLowerCase();
      if (!byCat[key]) {
        byCat[key] = { category: key, account_name: null, is_default: false, custom: false, count: c.count, total_amount: c.total_amount, unmapped: true };
      } else {
        byCat[key].count = c.count;
        byCat[key].total_amount = c.total_amount;
      }
    }
    // iter432 — Also pull every main + sub category from `expense-category-tree`
    // (the canonical CoA-side hierarchy). This guarantees an unmapped main/sub
    // category that's *defined but never used yet* still appears here so the
    // user can pre-link it before any expense is recorded against it.
    const walkTree = (nodes) => {
      for (const node of (nodes || [])) {
        const key = (node.name || '').toLowerCase();
        if (!byCat[key]) {
          byCat[key] = {
            category: key,
            account_name: null,
            is_default: false,
            custom: false,
            count: 0,
            total_amount: 0,
            unmapped: true,
            from_tree: true,
          };
        }
        if (node.children) walkTree(node.children);
      }
    };
    walkTree(tree);
    const list = Object.values(byCat);
    return list.sort((a, b) => {
      // Unmapped first, then custom, then defaults
      const rank = (r) => r.unmapped ? 0 : r.custom ? 1 : 2;
      const ra = rank(a); const rb = rank(b);
      if (ra !== rb) return ra - rb;
      return (b.total_amount || 0) - (a.total_amount || 0);
    });
  }, [mappings, defaults, categoriesInUse, tree]);

  const filtered = rows.filter(r => !search || r.category.toLowerCase().includes(search.toLowerCase()) || (r.account_name || '').toLowerCase().includes(search.toLowerCase()));

  const openEdit = (row) => {
    const acc = accounts.find(a => a.name === row.account_name);
    setEditing({
      category: row.category,
      account_id: acc?.id || '',
      account_name: row.account_name || '',
    });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditing({ category: '', account_id: '', account_name: '' });
    setShowDialog(true);
  };

  const save = async () => {
    if (!editing.category || !editing.account_id) {
      toast.error('Please select both a category and an account'); return;
    }
    try {
      const selectedAcc = accounts.find(a => a.id === editing.account_id);
      await api.post('/accounting/category-account-mappings', {
        category: editing.category,
        account_id: editing.account_id,
        account_name: selectedAcc?.name,
      });
      toast.success('Mapping saved');
      setShowDialog(false);
      loadAll();
      setReviewKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (category) => {
    if (!window.confirm(`Remove custom mapping for "${category}"? The default routing will apply.`)) return;
    try {
      await api.delete(`/accounting/category-account-mappings/${encodeURIComponent(category)}`);
      toast.success('Mapping removed');
      loadAll();
    } catch { toast.error('Remove failed'); }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    unmapped: rows.filter(r => r.unmapped).length,
    custom: rows.filter(r => r.custom).length,
  }), [rows]);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="category-mapping-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/chart-of-accounts')} data-testid="back-btn">
              <ArrowLeft size={18} />
            </Button>
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200/50">
              <LinkIcon size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Category → Account Mapping</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Control how expense categories are routed in auto-generated Journal Entries</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={groupUtilities} size="sm" variant="outline" disabled={autoMapping} data-testid="group-utilities-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> Fix Chart Structure
            </Button>
            <Button onClick={runRefix} size="sm" variant="outline" disabled={autoMapping} data-testid="refix-mappings-btn">
              <LinkIcon className="w-4 h-4 mr-1.5" /> {autoMapping ? 'Working…' : 'Re-fix mappings'}
            </Button>
            <Button onClick={runAutoMap} size="sm" variant="outline" disabled={autoMapping} data-testid="auto-map-btn">
              <LinkIcon className="w-4 h-4 mr-1.5" /> {autoMapping ? 'Mapping…' : 'Auto-Map'}
            </Button>
            <Button onClick={openNew} size="sm" data-testid="add-mapping-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> Add Mapping
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-border p-1 w-fit" data-testid="mapping-view-toggle">
          {[['simple', 'Simple view'], ['full', 'Full original view']].map(([k, l]) => (
            <Button key={k} size="sm" variant={view === k ? 'default' : 'ghost'} onClick={() => setView(k)} data-testid={`view-${k}-btn`}>{l}</Button>
          ))}
        </div>

        {view === 'simple' && (
          <MappingReviewView key={reviewKey} onEdit={(category, accountName) => openEdit({ category, account_name: accountName })} />
        )}

        {view === 'full' && (<>
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <Card className="border-border">
            <CardContent className="p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Categories</p>
              <p className="text-2xl font-bold text-foreground" data-testid="stat-total">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-warning/30 bg-warning/5">
            <CardContent className="p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-warning">Unmapped (in use)</p>
              <p className="text-2xl font-bold text-warning" data-testid="stat-unmapped">{stats.unmapped}</p>
              <p className="text-[10px] text-warning/80 mt-0.5">Falling back to "Miscellaneous Expenses"</p>
            </CardContent>
          </Card>
          <Card className="border-success/30 bg-success/5">
            <CardContent className="p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-success">Custom Mappings</p>
              <p className="text-2xl font-bold text-success" data-testid="stat-custom">{stats.custom}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search categories or accounts..." value={search}
                onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-input" />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No mappings found</div>
            ) : (
              <div className="divide-y divide-border">
                <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/30">
                  <div className="col-span-4">Expense Category</div>
                  <div className="col-span-4">Mapped Account</div>
                  <div className="col-span-2 text-right">Usage</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                {filtered.map((r) => {
                  // Find the matching node in the recursive tree (by raw name) so we
                  // can render subcategories + sub-sub under each main row.
                  const treeNode = tree.find(n => (n.raw || n.name?.toLowerCase()) === r.category);
                  const isExpanded = expandedKeys.has(r.category);
                  const hasSubs = treeNode && (treeNode.subcategories || []).length > 0;

                  const renderSubTree = (subs, level = 1) => (
                    <div className={`pl-${level * 6} bg-muted/10 border-t border-border/40`}>
                      {subs.map(s => (
                        <div key={s.id || s.name} className="flex items-center justify-between gap-3 px-4 py-2 text-xs hover:bg-muted/30" data-testid={`subcat-row-${s.id || s.name}`}>
                          <div className="flex items-center gap-2 flex-1" style={{ paddingLeft: `${level * 18}px` }}>
                            <ChevronRight size={12} className="text-muted-foreground/50" />
                            <span className="text-foreground">{s.name}</span>
                            {level === 1 && <Badge variant="outline" className="text-[9px] bg-primary/5 text-primary border-primary/20">Sub</Badge>}
                            {level >= 2 && <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">Sub-sub</Badge>}
                          </div>
                          <div className="flex items-center gap-1">
                            {level < 2 && (
                              <Button size="sm" variant="ghost" className="h-7 text-[10px] gap-1"
                                onClick={() => { setShowSubDialog({ parentId: s.id, parentName: s.name, level: level + 1 }); setNewSubName(''); }}
                                data-testid={`add-sub-${s.id}`}>
                                <Plus size={11} /> Add deeper
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 text-error hover:bg-error/10"
                              onClick={() => deleteSubCategory(s)}
                              data-testid={`delete-sub-${s.id}`}>
                              <Trash2 size={11} />
                            </Button>
                          </div>
                          {/* Recurse */}
                          {(s.subcategories || []).length > 0 && (
                            <div className="w-full">{renderSubTree(s.subcategories, level + 1)}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  );

                  return (
                  <div key={r.category} data-testid={`mapping-row-${r.category}`}>
                    <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm hover:bg-muted/20">
                    <div className="col-span-4 flex items-center gap-2">
                      <button
                        onClick={() => toggleExpand(r.category)}
                        className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                        title={hasSubs ? 'Toggle subcategories' : 'No subcategories'}
                        data-testid={`expand-${r.category}`}
                      >
                        {hasSubs ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <ChevronRight size={14} className="opacity-30" />}
                      </button>
                      <span className="font-medium text-foreground capitalize">{r.category.replace(/_/g, ' ')}</span>
                      {r.unmapped && (
                        <Badge variant="outline" className="text-[10px] bg-warning/10 text-warning border-warning/30">
                          <AlertCircle size={10} className="mr-1" /> Unmapped
                        </Badge>
                      )}
                      {r.custom && (
                        <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">Custom</Badge>
                      )}
                      {r.is_default && !r.custom && (
                        <Badge variant="outline" className="text-[10px] bg-info/10 text-info border-info/30">Default</Badge>
                      )}
                      {hasSubs && (
                        <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground border-border">{(treeNode.subcategories || []).length} sub</Badge>
                      )}
                    </div>
                    <div className="col-span-4">
                      {r.account_name ? (
                        <span className="text-foreground">{r.account_name}</span>
                      ) : (
                        <span className="text-muted-foreground italic">Miscellaneous Expenses (fallback)</span>
                      )}
                    </div>
                    <div className="col-span-2 text-right">
                      {r.count ? (
                        <div className="text-xs">
                          <div className="font-medium text-foreground">{r.count} txn{r.count !== 1 ? 's' : ''}</div>
                          <div className="text-muted-foreground">{cur()} {(r.total_amount || 0).toLocaleString()}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      {treeNode && (
                        <Button size="sm" variant="ghost" className="h-8 text-xs gap-1"
                          onClick={() => {
                            // Find or auto-create the parent category id, then open dialog
                            const rootId = treeNode.id || (categoriesInUse.find(c => c.category === r.category)?.id);
                            if (treeNode.id) {
                              setShowSubDialog({ parentId: treeNode.id, parentName: treeNode.name, level: 1 });
                              setNewSubName('');
                            } else {
                              // Need to first create the root expense category, then add sub
                              api.post('/categories', { name: treeNode.name, type: 'expense' })
                                .then(res => {
                                  setShowSubDialog({ parentId: res.data.id, parentName: treeNode.name, level: 1 });
                                  setNewSubName('');
                                  loadAll();
                                })
                                .catch(() => toast.error('Could not create root category'));
                            }
                          }}
                          data-testid={`add-sub-btn-${r.category}`}>
                          <Plus size={12} /> Sub
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="h-8 text-xs" data-testid={`edit-${r.category}`}>
                        {r.custom ? 'Edit' : 'Override'}
                      </Button>
                      {r.custom && (
                        <Button size="sm" variant="ghost" onClick={() => remove(r.category)} className="h-8 text-error hover:bg-error/10" data-testid={`delete-${r.category}`}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                    </div>
                    {isExpanded && hasSubs && renderSubTree(treeNode.subcategories, 1)}
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        </>)}

        {/* Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="mapping-dialog">
            <DialogHeader>
              <DialogTitle>{editing.account_name ? 'Edit Mapping' : 'Add Mapping'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground">Expense Category</label>
                <Input value={editing.category}
                  onChange={e => setEditing({ ...editing, category: e.target.value.toLowerCase() })}
                  placeholder="e.g. marketing, rent, salary…"
                  data-testid="category-input" />
                <p className="text-xs text-muted-foreground mt-1">Lowercased. Matches whatever is stored on expenses.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">Route to Account</label>
                <Select value={editing.account_id} onValueChange={v => setEditing({ ...editing, account_id: v })}>
                  <SelectTrigger data-testid="account-select"><SelectValue placeholder="Select Chart of Accounts entry" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map(a => (
                      <SelectItem key={a.id} value={a.id} data-testid={`account-option-${a.code}`}>
                        {a.code ? `[${a.code}] ` : ''}{a.name} <span className="text-[10px] text-muted-foreground ml-1">({a.type})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Journal Entries for this category will debit the selected account.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-btn">Cancel</Button>
              <Button onClick={save} data-testid="save-btn"><Save size={14} className="mr-1.5" /> Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Add Sub-Category Dialog */}
        <Dialog open={!!showSubDialog} onOpenChange={(open) => { if (!open) { setShowSubDialog(null); setNewSubName(''); } }}>
          <DialogContent className="max-w-md" data-testid="add-sub-dialog">
            <DialogHeader>
              <DialogTitle>
                Add {showSubDialog?.level === 2 ? 'Sub-sub-category' : 'Subcategory'}
                <span className="block text-xs font-normal text-muted-foreground mt-1">Under: <strong>{showSubDialog?.parentName}</strong></span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                placeholder="e.g. Saudi Visa, Iqama, GOSI Fee..."
                value={newSubName}
                onChange={e => setNewSubName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addSubCategory(); }}
                autoFocus
                data-testid="new-subcat-name"
              />
              <p className="text-[11px] text-muted-foreground">
                {showSubDialog?.level === 2
                  ? 'Sub-sub-categories let you split your existing subcategory even further (e.g., Visa → Saudi Visa → 1st Year).'
                  : 'Subcategories help group expenses while still rolling up to the same Account.'}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowSubDialog(null); setNewSubName(''); }}>Cancel</Button>
              <Button onClick={addSubCategory} data-testid="save-subcat-btn"><Save size={14} className="mr-1.5" /> Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
